import {
  noticeApi,
  type ParentsAppNoticeCreateBody,
  type ParentsAppNoticeListParams,
} from '@mathflat/domain/Notice'
import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import { isBefore } from 'date-fns'
import type { PopupLocalStorageData } from 'src/constant'
import type { ParamDataT } from 'src/dto/common/ReqParams'

import { mpEventInfo } from '@/constant'
import type { Academy, ReqParams, User } from '@/dto'
import { AcademyNotice, Banner, NoticeColl, ParentsAppNotice } from '@/dto'
import ExtendedMap from '@/models/ExtendedMap'
import commonRepo from '@/repository/common.repo.ts'
import { localStorageService } from '@/service/storage.service'

/** hcms 알림판 target2 컴포넌트의 키와 1:1 */
export type NoticeBrandStatus = 'B2B' | 'B2G' | 'CREVERSE' | 'VINE'

export type FetchPopupProps = {
  teacherId: User['teacherId']
  academyStatus: Academy['status']
  academyMetaData: Academy['metadata']
  academyAuthority: Academy['authority']
  brandStatus: NoticeBrandStatus
}

export const resolveNoticeBrandStatus = ({
  isCreverse,
  isVine,
  isSchoolflat,
}: {
  isCreverse: boolean
  isVine: boolean
  isSchoolflat: boolean
}): NoticeBrandStatus => {
  if (isCreverse) return 'CREVERSE'
  if (isVine) return 'VINE'
  if (isSchoolflat) return 'B2G'
  return 'B2B'
}

export class NoticeController {
  private toParentsAppNoticeBody({
    title,
    type,
    content,
    marked,
  }: ParentsAppNotice): ParentsAppNoticeCreateBody {
    return { title, type, content: domainProvider.replaceAllMathFlatDomain(content), marked }
  }

  // 로컬호스트에서 다시보지 않음에 체크 된 팝업의 id를 가져온다.
  private excludedPopupIdsFromLocalStorage(storageKey: string) {
    const storedPopups = JSON.parse(
      localStorage.getItem(storageKey) ?? '[]',
    ) as PopupLocalStorageData[]

    // 1. localStorage에서 strapi fetch에서 제외할 팝업들을 구한다.
    const excludedPopups = storedPopups.filter(({ expiredDate }) => {
      return expiredDate === 'infinite' || isBefore(new Date(), new Date(expiredDate))
    })

    // 2. 제외된 팝업은 expiredDate가 지난 것들이 삭제된 최신의 값과 같으므로, 겸사겸사 localStorage값을 리뉴얼 시켜준다.
    localStorage.setItem(storageKey, JSON.stringify(excludedPopups))

    // 3. id만 추출
    return excludedPopups.map(({ id }) => id)
  }
  private makeTargetFilter({
    academyStatus,
    popupTargetingGroup,
    academyAuthority,
    excludedIds,
  }: {
    academyStatus: FetchPopupProps['academyStatus']
    popupTargetingGroup: FetchPopupProps['academyMetaData']['popupTargetingGroup']
    academyAuthority: FetchPopupProps['academyAuthority']
    excludedIds: number[]
  }) {
    const academyGroup =
      popupTargetingGroup && popupTargetingGroup !== '' ? popupTargetingGroup.split(',') : []

    // group에 대괄호를 양옆에 넣는 이유: strapi에서 contains 필터를 사용 시, 문자열로 includes 여부를 판단하기 때문에 중복된 string의 값이 있는 그룹의 경우 필터링이 제대로 되지 않기 때문.
    // 따라서 strapi에서도 include와 exclude의 필드에 각 그룹을 대괄호로 감싸서 값을 넣어주어야함.
    const includeFilterList = academyGroup.map((group) => ({
      include: { $contains: `[${group}]` }, // ex. { $contains: [초등] }
    }))

    const excludeFilterList = academyGroup.map((group) => ({
      exclude: { $notContains: `[${group}]` },
    }))

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const filters: { $and: any } = {
      $and: [
        { id: { $notIn: excludedIds } },
        {
          target: {
            [academyStatus]: true,
          },
        },
        { $or: [{ authority: { [academyAuthority]: true } }, { authority: { $null: true } }] },
        { $or: [...includeFilterList, { include: { $null: true } }] },
      ],
    }

    if (excludeFilterList.length > 0) {
      filters.$and.push({
        $or: [{ $and: [...excludeFilterList] }, { exclude: { $null: true } }],
      })
    }
    return filters
  }
  private makeNoticeBoardTargetFilter({
    brandStatus,
    academyStatus,
    popupTargetingGroup,
    academyAuthority,
  }: {
    brandStatus: FetchPopupProps['brandStatus']
    academyStatus: FetchPopupProps['academyStatus']
    popupTargetingGroup: FetchPopupProps['academyMetaData']['popupTargetingGroup']
    academyAuthority: FetchPopupProps['academyAuthority']
  }) {
    const academyGroup =
      popupTargetingGroup && popupTargetingGroup !== '' ? popupTargetingGroup.split(',') : []

    // group에 대괄호를 양옆에 넣는 이유: strapi에서 contains 필터를 사용 시, 문자열로 includes 여부를 판단하기 때문에 중복된 string의 값이 있는 그룹의 경우 필터링이 제대로 되지 않기 때문.
    // 따라서 strapi에서도 include와 exclude의 필드에 각 그룹을 대괄호로 감싸서 값을 넣어주어야함.
    const includeFilterList = academyGroup.map((group) => ({
      include: { $contains: `[${group}]` }, // ex. { $contains: [초등] }
    }))

    const excludeFilterList = academyGroup.map((group) => ({
      exclude: { $notContains: `[${group}]` },
    }))

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const filters: { $and: any } = {
      $and: [
        {
          target: {
            [academyStatus]: true,
          },
          target2: {
            [brandStatus]: true,
          },
        },
        { $or: [{ authority: { [academyAuthority]: true } }, { authority: { $null: true } }] },
        { $or: [...includeFilterList, { include: { $null: true } }] },
      ],
    }

    if (excludeFilterList.length > 0) {
      filters.$and.push({
        $or: [{ $and: [...excludeFilterList] }, { exclude: { $null: true } }],
      })
    }
    return filters
  }

  // 공지 및 업데이트
  async fetchAcademyNotices(reqParams: ReqParams<ParamDataT>) {
    const { content, last } = await noticeApi.getAcademyNotices(
      {
        sort: ['createdAt:desc'],
        pagination: { page: reqParams.params.page, pageSize: reqParams.params.size },
      },
      { mp_event_name: mpEventInfo.공지사항 },
    )
    const academyNoticeArr = content.map(
      (notice) =>
        new AcademyNotice({
          ...notice,
          content: domainProvider.replaceAllMathFlatDomain(notice.content),
        }),
    )

    return {
      academyNoticeMap: new NoticeColl(ExtendedMap.fromArr(academyNoticeArr, 'id')),
      last,
    }
  }

  // 신규 매쓰플랫 알림판
  async fetchNoticeBoard({
    academyStatus,
    academyMetaData,
    academyAuthority,
    brandStatus,
  }: FetchPopupProps) {
    const filters = this.makeNoticeBoardTargetFilter({
      brandStatus,
      academyStatus,
      popupTargetingGroup: academyMetaData.popupTargetingGroup,
      academyAuthority,
    })

    const noticeBoards = await noticeApi.getNoticeBoards(
      { sort: ['publishedAt:desc'], filters },
      { mp_event_name: mpEventInfo.이달의업데이트팝업 },
    )

    return noticeBoards.map((noticeBoard) => ({
      ...noticeBoard,
      content: domainProvider.replaceAllMathFlatDomain(noticeBoard.content),
      media: noticeBoard.media.map((media) => ({
        ...media,
        url: domainProvider.replaceAllMathFlatDomain(media.url),
      })),
    }))
  }

  // 팝업 - 온보딩
  /** deprecated 인듯..? */
  async fetchOnboardingPopup(teacherId: User['teacherId']) {
    const STORAGE_KEY = `ONBOARDING_POPUP_READ_${teacherId}` as const
    const excludedNames = localStorageService.getItem(STORAGE_KEY) ?? []

    return noticeApi.getOnboardingPopups({
      sort: ['priority:desc'],
      filters: { name: { $notIn: excludedNames } },
    })
  }

  // 무료체험 툴팁 배너
  async fetchBanner(user: User, isSchoolflat: boolean, excludeIds?: Banner['id'][]) {
    const filters = {
      $and: [
        {
          $not: { id: { $in: { ...excludeIds } } },
        },
        {
          expiredAt: { $gt: new Date().toISOString() },
        },
        { academyTypeTarget: isSchoolflat ? { B2G: true } : { B2B: true } },
      ],
    }
    const [bannerData] = await noticeApi.getBanners({
      sort: ['priority:desc'],
      filters,
      pagination: {
        limit: 1,
      },
    })

    if (!bannerData) {
      if (commonRepo.isSchoolflat) return
      return new Banner({ user })
    }
    return new Banner({
      user,
      id: String(bannerData.id),
      data: {
        ...bannerData,
        tooltipImageUrl: domainProvider.replaceMathFlatDomain(bannerData.tooltipImageUrl),
        longImageUrl: domainProvider.replaceMathFlatDomain(bannerData.longImageUrl),
        redirectUrl: domainProvider.replaceMathFlatDomain(bannerData.redirectUrl ?? ''),
      },
    })
  }

  async fetchUpdatedContents() {
    return noticeApi.getUpdatedContents()
  }

  // 학부모 앱 공지
  async fetchParentsAppNotices(params: ParentsAppNoticeListParams) {
    const { content, last } = await noticeApi.getParentsAppNotices(params, {
      mp_event_name: mpEventInfo.학부모앱공지사항조회,
    })
    const parentsAppNotices = content.map((notice) => new ParentsAppNotice(notice))
    const parentsAppNoticeMap = ExtendedMap.fromArr(parentsAppNotices, 'id')
    return { parentsAppNotices: new NoticeColl(parentsAppNoticeMap), last }
  }

  async createParentsAppNotice(parentsAppNotice: ParentsAppNotice) {
    await noticeApi.createParentsAppNotice(this.toParentsAppNoticeBody(parentsAppNotice), {
      mp_event_name: mpEventInfo.학부모앱공지사항등록,
    })
  }

  async changeParentsAppNotice(parentsAppNotice: ParentsAppNotice) {
    await noticeApi.updateParentsAppNotice(
      parentsAppNotice.id,
      this.toParentsAppNoticeBody(parentsAppNotice),
      { mp_event_name: mpEventInfo.특정학부모앱공지사항수정 },
    )
  }

  async deleteParentsAppNotice(parentsAppNoticeIds: ParentsAppNotice['id'][]) {
    await noticeApi.deleteParentsAppNotices(parentsAppNoticeIds, {
      mp_event_name: mpEventInfo.학부모앱공지사항삭제,
    })
  }

  async fetchServerMaintenanceNotice() {
    const serverMaintenance = await noticeApi.getServerMaintenance({
      mp_event_name: mpEventInfo.서버점검조회,
    })
    return {
      ...serverMaintenance,
      startDate: new Date(serverMaintenance.startDate),
      endDate: new Date(serverMaintenance.endDate),
    }
  }
}

export const noticeController = new NoticeController()
